import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { api, LitSquadDefinitionSummary, LitSquadMintResult } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { useLitSquadDefinitions } from '../hooks/useLitSquadDefinitions';

export function AdminPage() {
  return (
    <div className="max-w-4xl mx-auto px-4">
      <Link to="/a/arche_litsquad">
        <Button variant="ghost" size="sm" className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Inventory
        </Button>
      </Link>
      <h1 className="text-2xl font-bold mb-2">LitSquad Admin</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Manage species-level definitions and mint one-off NFTs to users.
      </p>

      <Tabs defaultValue="definitions">
        <TabsList>
          <TabsTrigger value="definitions">Definitions</TabsTrigger>
          <TabsTrigger value="mint">Mint</TabsTrigger>
        </TabsList>
        <TabsContent value="definitions" className="mt-4">
          <DefinitionsPanel />
        </TabsContent>
        <TabsContent value="mint" className="mt-4">
          <MintPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DefinitionsPanel() {
  const { definitions, total, loading, error } = useLitSquadDefinitions({ pageSize: 50 });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Species Definitions</CardTitle>
        <CardDescription>{total} species registered</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {error && <p className="text-sm text-destructive">{error}</p>}
        {loading && definitions.length === 0 ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          definitions.map((def) => <DefinitionRow key={def.itemDefId} def={def} />)
        )}
      </CardContent>
    </Card>
  );
}

function DefinitionRow({ def }: { def: LitSquadDefinitionSummary }) {
  return (
    <div className="flex items-center justify-between rounded-md border px-3 py-2">
      <div>
        <p className="font-medium text-sm">{def.name}</p>
        <p className="text-xs text-muted-foreground font-mono">{def.itemDefId}</p>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant="outline">{def.fungibility}</Badge>
        <Badge variant={def.status === 'active' ? 'default' : 'secondary'}>{def.status}</Badge>
        <span className="text-xs text-muted-foreground">Supply: {def.currentSupply}</span>
      </div>
    </div>
  );
}

function MintPanel() {
  const [itemDefId, setItemDefId] = useState('');
  const [toUsername, setToUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [thumbnailUri, setThumbnailUri] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<LitSquadMintResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onMint = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const out = await api.mintLitSquadItems({
        itemDefId: itemDefId.trim(),
        toUsername: toUsername.trim(),
        quantity: 1,
        displayName: displayName.trim() || undefined,
        thumbnailUri: thumbnailUri.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      setResult(out);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Mint failed');
    } finally {
      setSubmitting(false);
    }
  }, [itemDefId, toUsername, displayName, thumbnailUri, notes]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Mint LitSquad NFT</CardTitle>
        <CardDescription>
          Mint a single NFT for a user. Supply the species `item_def_id` (e.g.
          <code className="ml-1 text-xs">litdef_species_elephant</code>) plus optional per-item name
          and thumbnail overrides.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="itemDefId">Species item_def_id</Label>
          <Input
            id="itemDefId"
            value={itemDefId}
            onChange={(e) => setItemDefId(e.target.value)}
            placeholder="litdef_species_elephant"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="toUsername">Recipient Username</Label>
          <Input
            id="toUsername"
            value={toUsername}
            onChange={(e) => setToUsername(e.target.value)}
            placeholder="username"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="displayName">Display Name (optional)</Label>
          <Input
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="LitSquad Elephant Baseball Cleats"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="thumbnailUri">Thumbnail URL (optional)</Label>
          <Input
            id="thumbnailUri"
            value={thumbnailUri}
            onChange={(e) => setThumbnailUri(e.target.value)}
            placeholder="https://cdn.example.com/dfe-icons/LitSquad/cleats.png"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="notes">Notes (optional)</Label>
          <Input
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Reason for admin mint..."
          />
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {result && (
          <p className="text-sm text-green-600">
            Minted {result.items.length} NFT(s). First item ID: {result.items[0]?.itemId}
          </p>
        )}
        <Button onClick={onMint} disabled={submitting || !itemDefId || !toUsername}>
          {submitting ? 'Minting...' : 'Mint'}
        </Button>
      </CardContent>
    </Card>
  );
}
